import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import {
  patientAssessmentsService,
  AssessmentApplicationFill,
  FillQuestion,
  FillAnswer,
} from '../services/patientAssessmentsService';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';

/**
 * Impressão da avaliação (RN-537i): todas os itens com as respostas dadas, agrupadas por
 * categoria (seção, em dois níveis), mais a trilha, o resultado calculado e o parecer.
 * Cabeçalho/rodapé vêm do TEMPLATE PADRÃO da entidade, como nas demais impressões (RN-534e).
 */
const AssessmentPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const applicationId = getParam('applicationId') || '';

  const [fill, setFill] = useState<AssessmentApplicationFill | null>(null);
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'assessment');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão de Avaliação';
    const load = async () => {
      try {
        const data = await patientAssessmentsService.getFill(applicationId);
        setFill(data);
        const [patientData, templates] = await Promise.all([
          patientsService.getById(data.patientId).catch(() => null),
          // Sem template (ou sem permissão), imprime sem cabeçalho/rodapé — não falha
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setPatient(patientData);
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar a avaliação para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  const timeOf = (iso: string): string => iso.slice(11, 16);
  const stamp = (iso: string | null, by: string | null): string =>
    iso ? `${formatIsoDate(iso)} ${timeOf(iso)}${by ? ` por ${by}` : ''}` : '—';

  /** Blocos na mesma ordem da tela: raiz, depois cada seção mãe com suas filhas. */
  const blocks = useMemo(() => {
    type Block = { title: string; questions: FillQuestion[] };
    if (!fill) return [] as Block[];
    const list: Block[] = [];
    const questionsOf = (sectionId: string | null) =>
      fill.questions.filter(q => q.sectionId === sectionId);

    const root = questionsOf(null);
    if (root.length > 0) {
      list.push({ title: 'Itens iniciais', questions: root });
    }

    const parents = fill.sections.filter(s => s.parentSectionId === null);
    // Com UMA única mãe, o nome dela não distingue nada (todas os itens estão sob ela) e
    // só ocupa espaço; com várias — Portage: Socialização, Cognição… — o caminho informa
    const showParentInPath = parents.length > 1;

    parents.forEach(parent => {
      const children = fill.sections.filter(c => c.parentSectionId === parent.id);
      const own = questionsOf(parent.id);

      if (own.length > 0 || children.length === 0) {
        list.push({ title: parent.title, questions: own });
      }

      children.forEach(child => {
        list.push({
          title: showParentInPath ? `${parent.title} > ${child.title}` : child.title,
          questions: questionsOf(child.id),
        });
      });
    });
    return list;
  }, [fill]);

  /** Rótulo com o valor entre parênteses quando a opção pontua — é o que explica o resultado. */
  const answerOf = (question: FillQuestion): string => {
    const answer: FillAnswer | undefined = fill?.answers.find(a => a.questionId === question.id);
    if (!answer) return '—';
    if (answer.optionIds.length > 0) {
      const labels = answer.optionIds
        .map(id => question.options.find(o => o.id === id))
        .filter((o): o is NonNullable<typeof o> => !!o)
        .map(o => (o.value !== null ? `${o.label} (${o.value})` : o.label));
      return labels.join(', ') || '—';
    }
    return answer.textValue?.trim() || '—';
  };

  /** Soma dos valores marcados no item; nulo quando nenhuma opção pontua. */
  const scoreOf = (question: FillQuestion): number | null => {
    const answer = fill?.answers.find(a => a.questionId === question.id);
    if (!answer || answer.optionIds.length === 0) return null;
    const values = answer.optionIds
      .map(id => question.options.find(o => o.id === id)?.value)
      .filter((v): v is number => v !== null && v !== undefined);
    return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
  };

  const commentOf = (question: FillQuestion): string | null =>
    fill?.answers.find(a => a.questionId === question.id)?.comment?.trim() || null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !fill) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Avaliação não encontrada.'}
      </Typography>
    );
  }

  let number = 0;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      {/* Cabecalho e rodape em TODAS as paginas por thead/tfoot: o navegador repete o
          grupo E reserva a altura dele em cada folha. A tentativa anterior, com position
          fixed puxado para dentro da margem do @page, repetia no lugar errado e o Chrome
          simplesmente NAO desenhava os dois (regressao vista pelo Hilton em hml). */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .assessment-print-page {
            padding: 0 !important;
            width: auto !important;
            min-height: auto !important;
            box-shadow: none !important;
          }
          .print-frame { width: 100%; border-collapse: collapse; }
          .print-frame > thead { display: table-header-group; }
          .print-frame > tfoot { display: table-footer-group; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td,
          .print-frame > tfoot > tr > td { padding: 0; }
          .print-frame-header img { max-height: 18mm !important; }
          tr { break-inside: avoid; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
        >
          Imprimir
        </Button>
      </Box>

      <Box
        className="assessment-print-page"
        sx={{
          width: '210mm',
          maxWidth: '100%',
          minHeight: '297mm',
          padding: '15mm',
          paddingBottom: '30mm',
          boxSizing: 'border-box',
          background: 'white',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 0 8px rgba(0,0,0,0.15)',
          fontSize: '12pt',
          color: '#000',
          '@media print': { boxShadow: 'none', width: 'auto', minHeight: 'auto', margin: 0 },
        }}
      >
        <table className="print-frame">
          <thead><tr><td>
            {template && (
<div className="print-frame-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
                <div style={{ flexShrink: 0 }}>
                  {template?.file ? (
                    <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
                  ) : null}
                </div>
                <div
                  style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
                />
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
            </div>
)}
          </td></tr></thead>

          <tfoot><tr><td>
            {template && (
<div
              style={{ textAlign: 'center', fontSize: '9pt', paddingTop: '6mm' }}
              dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
            />
)}
          </td></tr></tfoot>

          <tbody><tr><td>

        <Box sx={{ textAlign: 'center', mb: '6mm' }}>
          <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
            {fill.formTitle}
          </Typography>
          <Typography sx={{ fontSize: '10pt', color: '#495057' }}>
            Avaliação — versão {fill.formVersion}
          </Typography>
        </Box>

        <Box sx={{ mb: '6mm' }}>
          <Box sx={{ display: 'flex', gap: '3rem', mb: '4px' }}>
            <Typography sx={{ fontSize: '11px', color: '#212529' }}>
              Paciente: {fill.patientName || patient?.patientName || '—'}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#212529' }}>
              Data Nasc.: {patient?.patientBirthDate ? formatIsoDate(patient.patientBirthDate.slice(0, 10)) : '—'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
            Responsável: {patient?.responsibleName1 || '—'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
            Data de emissão: {new Date().toLocaleDateString('pt-BR')}
          </Typography>
          {fill.status !== 'completed' && (
            <Typography sx={{ fontSize: '10px', color: '#b26a00', fontWeight: 700, mb: '4px' }}>
              {fill.isReopened
                ? 'Avaliação reaberta para edição — ainda não concluída novamente.'
                : 'Avaliação em andamento — ainda não concluída.'}
            </Typography>
          )}
          <Typography sx={{ fontSize: '10px', color: '#6c757d' }}>
            Solicitada: {stamp(fill.requestedAt, fill.requestedByName)} · Iniciada:{' '}
            {stamp(fill.startedAt, fill.startedByName)} · Finalizada:{' '}
            {stamp(fill.completedAt, fill.completedByName)}
          </Typography>
        </Box>

        {/* Um quadro POR CATEGORIA, no layout das demais impressões (cabeçalho + linhas
            zebradas): o espaço entre eles separa a leitura e evita a categoria partir ao meio */}
        <Box>
          {blocks.map(block => (
            <Box key={block.title} sx={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden', mb: '4mm', breakInside: 'avoid' }}>
              <Box sx={{ backgroundColor: '#e9ecef', borderBottom: '1px solid #dee2e6', p: '6px 8px', breakAfter: 'avoid' }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#212529' }}>
                  {block.title}
                </Typography>
              </Box>

              {block.questions.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 60px', backgroundColor: '#f1f3f5', borderBottom: '1px solid #dee2e6', breakInside: 'avoid', breakAfter: 'avoid' }}>
                  <Box sx={{ p: '6px 8px', borderRight: '1px solid #dee2e6' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#495057' }}>Item</Typography>
                  </Box>
                  <Box sx={{ p: '6px 8px', borderRight: '1px solid #dee2e6' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#495057' }}>Resposta</Typography>
                  </Box>
                  <Box sx={{ p: '6px 8px', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#495057' }}>Valor</Typography>
                  </Box>
                </Box>
              )}

              {block.questions.length === 0 ? (
                <Box sx={{ p: '8px', borderBottom: '1px solid #dee2e6' }}>
                  <Typography sx={{ fontSize: '10px', color: '#6c757d' }}>Sem itens nesta categoria.</Typography>
                </Box>
              ) : (
                block.questions.map(question => {
                  number += 1;
                  const comment = commentOf(question);
                  return (
                    <Box
                      key={question.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 60px',
                        borderBottom: '1px solid #dee2e6',
                        breakInside: 'avoid',
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                      }}
                    >
                      <Box sx={{ p: '10px', borderRight: '1px solid #dee2e6' }}>
                        <Typography sx={{ fontSize: '11px', color: '#212529', lineHeight: 1.4 }}>
                          {number}. {question.text}
                        </Typography>
                      </Box>
                      <Box sx={{ p: '10px', borderRight: '1px solid #dee2e6' }}>
                        <Typography sx={{ fontSize: '11px', color: '#212529', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.4 }}>
                          {answerOf(question)}
                        </Typography>
                        {comment && (
                          <Typography sx={{ fontSize: '10px', color: '#6c757d', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: '2px' }}>
                            <strong>Comentário:</strong> {comment}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ p: '10px', textAlign: 'right' }}>
                        {/* Traço = opção sem pontuação (ou item de texto). Vazio ≠ zero:
                            um 0 pontua zero e entra na média; sem valor fica fora da conta */}
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: scoreOf(question) === null ? '#adb5bd' : '#212529' }}>
                          {scoreOf(question) ?? '—'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          ))}
        </Box>

        {fill.results.length > 0 && (
          <Box sx={{ mt: '6mm', border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden', breakInside: 'avoid' }}>
            <Box sx={{ backgroundColor: '#f1f3f5', borderBottom: '1px solid #dee2e6', p: '8px' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#212529' }}>Resultado</Typography>
            </Box>
            <Box sx={{ p: '10px' }}>
            {/* Reaberta e alterada: o papel PRECISA dizer que o número não corresponde mais às
                respostas, senão circula um documento que se contradiz por dentro */}
            {fill.resultOutdated && (
              <Typography sx={{ fontSize: '10px', color: '#b26a00', fontWeight: 700, mb: '4px' }}>
                Atenção: as respostas foram alteradas após este cálculo. O resultado abaixo é o da
                conclusão anterior e será atualizado quando a avaliação for concluída novamente.
              </Typography>
            )}
            {fill.results.map(r => (
              <Typography key={`${r.sectionId ?? 'geral'}-${r.calcTypeCode}`} sx={{ fontSize: '11px', color: '#212529' }}>
                {r.sectionTitle ?? 'Resultado geral'} — {r.calcTypeName}:{' '}
                <strong>
                  {r.value !== null
                    ? r.value
                    : r.breakdown.length > 0
                      ? r.breakdown.map(i => `${i.label}: ${i.value}`).join(' · ')
                      : '—'}
                </strong>
              </Typography>
            ))}
            {fill.interpretationGuide && (
              <Typography sx={{ fontSize: '10px', color: '#6c757d', mt: '2mm', whiteSpace: 'pre-wrap' }}>
                {fill.interpretationGuide}
              </Typography>
            )}
            </Box>
          </Box>
        )}

        {fill.professionalInterpretation && (
          <Box sx={{ mt: '5mm', border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden', breakInside: 'avoid' }}>
            <Box sx={{ backgroundColor: '#f1f3f5', borderBottom: '1px solid #dee2e6', p: '8px' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#212529' }}>
                Parecer do profissional
              </Typography>
            </Box>
            <Box sx={{ p: '10px' }}>
              <Typography sx={{ fontSize: '11px', color: '#212529', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
                {fill.professionalInterpretation}
              </Typography>
            </Box>
          </Box>
        )}

          </td></tr></tbody>
        </table>
      </Box>
    </Box>
  );
};

export default AssessmentPrint;
